<template>
  <h2>{{ t("components.siteSettings.pins.title") }}</h2>
  <v-card
    v-for="(pin, index) in pinsForm.pins"
    :key="pin.role"
    elevation="3"
    class="mb-3 pb-3 my-5"
  >
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <div>
          <span class="font-weight-black text-capitalize"> {{ pin.role }}</span>
        </div>
        <v-btn-nd v-if="pin.mode === Mode.VIEW" variant="text" @click="pin.mode = Mode.EDIT">
          <v-icon>mdi-pencil</v-icon>
        </v-btn-nd>
        <v-btn-nd v-else variant="text" @click="pin.mode = Mode.VIEW">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn-nd>
      </div>
    </template>
    <v-card-text v-if="pin.mode === Mode.EDIT">
      <v-text-field
        v-model="pin.updatedPin"
        :error-messages="
          (v$.pins.$each.$response.$errors[index].updatedPin.map(
            (e: ErrorObject) => e.$message,
          ) as string[]) || []
        "
        :label="t('components.siteSettings.pins.pinLabel')"
        clearable
        @update:model-value="pin.isDefault = false"
      />
    </v-card-text>
    <v-card-text v-else>
      {{ pin.isDefault ? t("components.siteSettings.pins.defaultPin") : pin.pin }}
    </v-card-text>
  </v-card>
  <div class="d-flex justify-end">
    <v-btn :loading="loading" :disabled="!isPinEdited || v$.$invalid" @click="save">
      {{ t("components.siteSettings.pins.saveButton") }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import {
  SiteInternalSettingsPinsInner,
  SiteInternalSettingsPinsInnerRoleEnum,
  UpdateSiteDevicesPasscodesRequest,
} from "@tableyeti/merchant-service";
import useVuelidate, { ErrorObject } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useApplicationStore } from "@/store/application";

const { t } = useI18n();

enum Mode {
  VIEW = "view",
  EDIT = "edit",
}

interface Pin extends SiteInternalSettingsPinsInner {
  isDefault: boolean;
  mode: Mode;
  updatedPin?: string;
}
const { pins } = defineProps<{ pins?: Array<SiteInternalSettingsPinsInner> }>();

const { api, currentMerchantId } = useYetipayApi();
const applicationStore = useApplicationStore();
const siteId = useRoute().params.siteId as string;

const loading = ref(false);

const pinsForm = ref<{ pins: Array<Pin> }>({ pins: [] });

const toPinsForm = () => ({
  pins: Object.keys(SiteInternalSettingsPinsInnerRoleEnum).map((key) => {
    const pinFound = pins?.find(
      (pin) =>
        pin.role ===
        SiteInternalSettingsPinsInnerRoleEnum[
          key as keyof typeof SiteInternalSettingsPinsInnerRoleEnum
        ],
    );
    if (pinFound) {
      return {
        ...pinFound,
        isDefault: false,
        mode: Mode.VIEW,
        updatedPin: "",
      };
    }

    return {
      role: SiteInternalSettingsPinsInnerRoleEnum[
        key as keyof typeof SiteInternalSettingsPinsInnerRoleEnum
      ],
      isDefault: true,
      pin: "",
      mode: Mode.VIEW,
      updatedPin: "",
    };
  }),
});

pinsForm.value = toPinsForm();

const v$ = useVuelidate<{ pins: Array<Pin> }>(
  {
    pins: {
      $each: helpers.forEach({
        updatedPin: {
          pin: helpers.withMessage(
            t("components.siteSettings.pins.validation.pinFormat"),
            helpers.regex(/^[0-9]{4,6}$/),
          ),
        },
      }),
    },
  },
  pinsForm,
);

watch(
  () => pins,
  () => {
    pinsForm.value = toPinsForm();
  },
);

const pinMap: Record<
  SiteInternalSettingsPinsInnerRoleEnum,
  keyof UpdateSiteDevicesPasscodesRequest
> = {
  [SiteInternalSettingsPinsInnerRoleEnum.Settings]: "adminMenuPin",
  [SiteInternalSettingsPinsInnerRoleEnum.Transactions]: "txMenuPin",
};

const emit = defineEmits(["settingsUpdate"]);

const save = async () => {
  const pinsToUpdate = pinsForm.value.pins
    .filter((p) => !p.isDefault)
    .reduce((acc: Partial<Record<keyof UpdateSiteDevicesPasscodesRequest, string>>, next) => {
      if (next.updatedPin) {
        acc[pinMap[next.role]] = next.updatedPin;
      }
      return acc;
    }, {});

  if (Object.keys(pinsToUpdate).length > 0) {
    loading.value = true;
    try {
      await api.updateSiteDevicesPasscodes(currentMerchantId, siteId, pinsToUpdate);
      applicationStore.notifyUser({ message: t("sites.internalSettings.successMessage") });
      emit("settingsUpdate");
    } catch (error) {
      console.error("Error updating site pins settings:", error);
      applicationStore.notifyUser({
        message: t("components.siteSettings.pins.errorMessage"),
        type: "error",
      });
    } finally {
      loading.value = false;
    }
  }
};

const isPinEdited = computed(() => pinsForm.value.pins.some((pin) => pin.updatedPin));
</script>
